import type { EAppId, SettingsData } from "../../interfaces";
import { isValidKey, isValidValue } from "../ConfigurationData/ConfigurationData";

export function exportSettingsData(applicationId: EAppId, data: SettingsData): void {
    try {
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: "application/json" });
        const url = URL.createObjectURL(blob);

        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "");
        const a = document.createElement("a");
        a.href = url;
        a.download = `settings-${applicationId}-${timestamp}.json`;
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Failed to export settings:", error);
    }
}

export function importSettingsDataAsync(applicationId: EAppId): Promise<SettingsData | undefined> {
    return new Promise((resolve, reject) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".json";

        input.onchange = async (event) => {
            const file = (event.target as HTMLInputElement).files?.[0];
            if (!file) {
                reject("No file selected.");
                return;
            }

            try {
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const jsonData = reader.result as string;
                        const parsedData: SettingsData = JSON.parse(jsonData);

                        if (validateSettingsData(applicationId, parsedData)) {
                            resolve(parsedData);
                        } else {
                            reject("Invalid settings data format.");
                        }
                    } catch (error) {
                        console.error("Error parsing JSON:", error);
                        reject("Error parsing JSON.");
                    }
                };

                reader.onerror = () => {
                    reject("Error reading file.");
                };

                reader.readAsText(file);
            } catch (error) {
                console.error("Unexpected error:", error);
                reject("Unexpected error while processing the file.");
            }
        };

        input.click();
    });
}

/**
 * Validates the structure of the imported settings data.
 * @param applicationId The applicationId the settings data belongs to
 * @param data - The SettingsData to validate.
 * @returns True if valid, false otherwise.
 */
function validateSettingsData(applicationId: EAppId, data: SettingsData): boolean {
    if (typeof data !== "object" || Array.isArray(data)) {
        return false;
    }

    // check if the object has required keys and fields and they are of the correct type
    for (const key of Object.keys(data)) {
        if (!isValidKey(applicationId, key)) {
            console.error(`Invalid key '${key}' for application '${applicationId}'`);
            return false;
        }

        const value = data[key];
        if (!isValidValue(applicationId, key, value)) {
            console.error(`Invalid value '${value}' for key '${key}' for application '${applicationId}'`);
            return false;
        }
    }

    return true;
}
